import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGlobalState } from "../contexts/GlobalContext";

const sortTasksByDueDateAsc = (items) => {
  const toTimestamp = (task) => {
    if (!task?.dueDate) return Number.POSITIVE_INFINITY;
    const ts = new Date(task.dueDate).getTime();
    return Number.isNaN(ts) ? Number.POSITIVE_INFINITY : ts;
  };

  return [...items].sort((a, b) => toTimestamp(a) - toTimestamp(b));
};

function Dashboard() {
  const { state, fetchContacts } = useGlobalState();
  const navigate = useNavigate();
  const [openTasks, setOpenTasks] = useState([]);
  const [urgentTasks, setUrgentTasks] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);

  useEffect(() => {
    if (state.user?.id && state.contacts.length === 0) {
      fetchContacts();
    }
  }, [state.user]);

  useEffect(() => {
    if (!state?.contacts) return;
    const test = process.env.REACT_APP_TEST;
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday as start of week
    startOfWeek.setHours(0, 0, 0, 0);

    const recent = state.contacts.filter((c) => {
      const created = new Date(c.createdAt);
      return created >= startOfWeek;
    });

    const sortedRecent = [...recent].sort((a, b) =>
      b.createdAt?.localeCompare(a.createdAt)
    );
    setRecentContacts(sortedRecent);

    const allTasks = state.contacts.flatMap((contact) =>
      (contact.tasks || []).map((task) => {
        const fallbackName = `${contact.first_name ?? ""} ${
          contact.last_name ?? ""
        }`.trim();
        const normalizedTaskId =
          task.taskId ?? task.id ?? task.task_id ?? task.taskID;

        return {
          ...task,
          taskId: normalizedTaskId ?? task.taskId,
          contactId: contact.id ?? contact.contactId ?? contact.contact_id,
          contactName: contact.name || fallbackName || task.contactName,
        };
      })
    );

    setOpenTasks(sortTasksByDueDateAsc(allTasks.filter((task) => task.status === "open")));
    setUrgentTasks(
      sortTasksByDueDateAsc(allTasks.filter((t) => t.urgency === "high" && t.status === "open"))
    );
  }, [state.contacts]);

  const handleTaskClick = (taskIdentifier) => {
    if (!taskIdentifier) return;

    navigate("/tasks", { state: { focusTaskId: taskIdentifier } });
  };

  return (
    <div className="container mt-4">
      <h1 className="mb-3">Dashboard</h1>

      {/* KPI Cards */}
      <div className="row mb-4">
        <KPI label="Total Contacts" value={state.contacts.length} />
        <KPI label="Open Tasks" value={openTasks.length} />
        <KPI label="Urgent Tasks" value={urgentTasks.length} />
        <KPI label="New This Week" value={recentContacts.length} />
      </div>

      {/* Actions */}
      <div className="mb-4 d-flex gap-3">
        <Link className="btn btn-primary" to="/form" state={{ role: 'contact' }}>+ Add Contact</Link>
        <Link className="btn btn-outline-secondary" to="/tasks">View All Tasks</Link>
        <Link className="btn btn-outline-secondary" to="/contacts">View All Contacts</Link>
      </div>

      {/* Recent Contacts */}
      <div className="card mb-4">
        <div className="card-header">Recent Contacts</div>
        <ul className="list-group list-group-flush">
          {recentContacts.map((contact) => (
            <li key={contact.id} className="list-group-item d-flex justify-content-between">
              <div>
                <strong>{contact.name}</strong> <span className="text-muted">({contact.role})</span>
                <div className="text-muted small">{contact.email}</div>
              </div>
              <Link className="btn btn-sm btn-outline-primary" to={`/contact/${contact.id}`}>View</Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Urgent Tasks */}
      <div className="card">
        <div className="card-header bg-danger text-white">High Urgency Tasks</div>
        <ul className="list-group list-group-flush">
          {urgentTasks.length === 0 && (
            <li className="list-group-item text-muted">No urgent tasks</li>
          )}
          {urgentTasks.slice(0, 5).map((task, index) => {
            const normalizedTaskId =
              task.taskId ??
              task.id ??
              task.task_id ??
              task.taskID ??
              `urgent-${index}`;

            const dueDateLabel = task.dueDate
              ? new Date(task.dueDate).toLocaleDateString()
              : "No due date";

            return (
              <li
                key={normalizedTaskId}
                className="list-group-item d-flex justify-content-between align-items-center"
                role="button"
                tabIndex={0}
                onClick={() => handleTaskClick(normalizedTaskId)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleTaskClick(normalizedTaskId);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <div>
                  <strong>{task.taskType}</strong> — {task.taskDescription}
                  <div className="small text-muted">
                    Contact: {task.contactName || "Unknown"}
                  </div>
                  <div className="small text-muted">Due: {dueDateLabel}</div>
                </div>
                <span className="badge bg-danger align-self-center">High</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div className="col-md-3 col-sm-6 mb-3">
      <div className="card text-center shadow-sm">
        <div className="card-body">
          <h2 className="card-title">{value}</h2>
          <p className="card-text text-muted">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
